/**
 * Centralized logging utility with different levels and production safety
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogConfig {
  enableConsole: boolean;
  enableRemote: boolean;
  minLevel: LogLevel;
  remoteEndpoint?: string;
}

class Logger {
  private readonly config: LogConfig;
  private readonly levelOrder: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3
  };

  constructor(config: LogConfig) {
    this.config = config;
  }

  private shouldLog(level: LogLevel): boolean {
    return this.levelOrder[level] >= this.levelOrder[this.config.minLevel];
  }

  private formatMessage(level: LogLevel, message: string, data?: any): string {
    const timestamp = new Date().toISOString();
    const emoji = this.getLevelEmoji(level);
    
    let formattedMessage = `${timestamp} ${emoji} [${level.toUpperCase()}] ${message}`;
    
    if (data !== undefined) {
      formattedMessage += `\nData: ${JSON.stringify(data, null, 2)}`;
    }
    
    return formattedMessage;
  }

  private getLevelEmoji(level: LogLevel): string {
    switch (level) {
      case 'debug': return '🐛';
      case 'info': return 'ℹ️';
      case 'warn': return '⚠️';
      case 'error': return '❌';
      default: return '📝';
    }
  }

  private async sendToRemote(level: LogLevel, message: string, data?: any): Promise<void> {
    if (!this.config.enableRemote || !this.config.remoteEndpoint) {
      return;
    }

    try {
      await fetch(this.config.remoteEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          level,
          message,
          data,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: globalThis.location.href
        })
      });
    } catch (error) {
      // Fallback to console if remote logging fails
      console.warn('Failed to send log to remote endpoint:', error);
    }
  }

  debug(message: string, data?: any): void {
    this.log('debug', message, data);
  }

  info(message: string, data?: any): void {
    this.log('info', message, data);
  }

  warn(message: string, data?: any): void {
    this.log('warn', message, data);
  }

  error(message: string, data?: any): void {
    this.log('error', message, data);
  }

  private log(level: LogLevel, message: string, data?: any): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const formattedMessage = this.formatMessage(level, message, data);

    // Console logging
    if (this.config.enableConsole) {
      switch (level) {
        case 'debug':
          console.debug(formattedMessage);
          break;
        case 'info':
          console.info(formattedMessage);
          break;
        case 'warn':
          console.warn(formattedMessage);
          break;
        case 'error':
          console.error(formattedMessage);
          break;
      }
    }

    // Remote logging (async, non-blocking)
    if (this.config.enableRemote) {
      this.sendToRemote(level, message, data).catch(() => {
        // Silent fail for remote logging
      });
    }
  }

  // Specialized logging methods for common patterns
  apiRequest(url: string, method: string, data?: any): void {
    this.debug(`API Request: ${method} ${url}`, data);
  }

  private getLogLevelForStatus(status: number): LogLevel {
    if (status >= 400) return 'error';
    if (status >= 300) return 'warn';
    return 'debug';
  }

  apiResponse(url: string, status: number, data?: any): void {
    const level = this.getLogLevelForStatus(status);
    this.log(level, `API Response: ${status} ${url}`, data);
  }

  userAction(action: string, data?: any): void {
    this.info(`User Action: ${action}`, data);
  }

  performance(operation: string, duration: number, data?: any): void {
    const level = duration > 1000 ? 'warn' : 'debug';
    this.log(level, `Performance: ${operation} took ${duration}ms`, data);
  }
}

// Create logger instance based on environment
const getLogConfig = (): LogConfig => {
  const isDevelopment = import.meta.env.MODE === 'development';
  const isProduction = import.meta.env.MODE === 'production';

  return {
    enableConsole: isDevelopment, // Only in development
    enableRemote: isProduction,   // Only in production
    minLevel: isDevelopment ? 'debug' : 'warn', // More verbose in dev
    remoteEndpoint: import.meta.env.VITE_LOG_ENDPOINT, // Optional remote endpoint
  };
};

// Export singleton logger instance
export const logger = new Logger(getLogConfig());

// Export factory for custom loggers
export const createLogger = (config: Partial<LogConfig>): Logger => {
  const defaultConfig = getLogConfig();
  return new Logger({ ...defaultConfig, ...config });
};

// Convenience exports for quick access
export const { debug, info, warn, error, apiRequest, apiResponse, userAction, performance } = logger;
